import { format } from "date-fns";

export const DATE_WITH_TIME_FORMAT = "MM/dd/yyyy hh:mm a";

/** Number Formatters **/
export const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
}).format;

/** Currency Formatters **/
const _currentFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format;
export const currencyFormatter = (priceInCents?: number | null) => _currentFormatter((priceInCents ?? 0) / 100);

const _currencyFormatterWithCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format;
// Like `currencyFormatter` but preserves sub-dollar precision (e.g. 123456 → "$1,234.56").
// Use when the displayed value is editable and round-tripping through `dollarsToCents` matters.
export const currencyFormatterWithCents = (priceInCents?: number | null) =>
  _currencyFormatterWithCents((priceInCents ?? 0) / 100);

const _currentFormatterWithSign = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  signDisplay: "exceptZero",
}).format;
export const currencyFormatterWithSign = (priceInCents?: number) =>
  _currentFormatterWithSign((priceInCents ?? 0) / 100);

const _compactCurrentFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  notation: "compact",
}).format;
export const compactCurrencyFormatter = (priceInCents?: number) => _compactCurrentFormatter((priceInCents ?? 0) / 100);

/** Date Formatters **/
export function dateFormatter(date: string | Date, formatTemplate: string): string {
  if (typeof date === "string") return format(new Date(date), formatTemplate);
  return format(date, formatTemplate);
}

export function formatAmount(priceInCents: number | string): string {
  if (typeof priceInCents === "string") return priceInCents;
  return `${(priceInCents / 100).toLocaleString("EN", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  })}`;
}

export function formatDelta(deltaInCents: number | string): string {
  if (typeof deltaInCents === "string") return deltaInCents;
  const sign = deltaInCents > 0 ? "+ " : "";
  return `${sign}${(deltaInCents / 100).toLocaleString("EN", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  })}`;
}

// Parse an editable dollar string to integer cents (e.g. "$1,234.56" → 123456). Empty/invalid → 0.
// Strips "$" and grouping commas so values formatted by `currencyFormatter` round-trip cleanly.
export function dollarsToCents(value: string): number {
  if (!value) return 0;
  const parsed = parseFloat(value.replace(/[$,]/g, ""));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}
